import { useState } from "react";
import { formatDuration, isVideoFilename, videoMime } from "../../../utils";
import { mediaUrl } from "../../../mediaAuth";
import { useVideoPosterFrame } from "../../hooks/useVideoPosterFrame";
import type { MediaItem } from "../../../types";

interface Props {
  item: MediaItem;
  isFav: boolean;
  isSelected: boolean;
  selectMode: boolean;
  isNew: boolean;
  onView: () => void;
  onToggleSelect: () => void;
  onDelete: () => void;
}

export function GalleryCard({ item, isFav, isSelected, selectMode, isNew, onView, onToggleSelect, onDelete }: Props) {
  const isVid = isVideoFilename(item.filename);
  const [duration, setDuration] = useState<number | null>(null);
  // Grabs a poster frame for video cards without playing (videos have no
  // separate thumb URL like photos do) - seek to a tiny offset once
  // metadata loads, pause once the seek lands. Lazy-gated behind
  // IntersectionObserver (see the shared hook) rather than loading every
  // video card in the grid concurrently on mount. Real duration straight
  // off the video element once metadata loads, for the corner badge below.
  const videoRef = useVideoPosterFrame(item.filename, isVid, "gallery-card-poster", setDuration);

  function handleActivate() {
    if (selectMode) onToggleSelect();
    else onView();
  }

  return (
    <div className="col-6 col-sm-4 col-md-4 col-lg-3">
      <div
        className={`card-photo gallery-card position-relative${isSelected ? " is-selected" : ""}`}
        data-filename={item.filename}
        tabIndex={0}
        role="button"
        aria-label={`View ${item.filename}`}
        onClick={handleActivate}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleActivate();
        }}
      >
        {isVid ? (
          <video ref={videoRef} className="photo-img" preload="none" muted playsInline>
            <source src={mediaUrl(`/assets/media/${item.filename}`)} type={videoMime(item.filename)} />
          </video>
        ) : (
          <img
            src={mediaUrl(`/assets/thumbs/${item.filename}`)}
            alt={item.filename}
            className="photo-img"
            loading="lazy"
          />
        )}

        {isVid && (
          <span className="gallery-card-play" aria-hidden="true">
            <i className="bi bi-play-fill" />
          </span>
        )}

        {isVid && duration !== null && <span className="gallery-card-duration">{formatDuration(duration)}</span>}

        {isFav && (
          <div className="fav-heart-badge">
            <i className="bi bi-heart-fill" />
          </div>
        )}

        {isNew && !selectMode && <span className="gallery-new-badge">New</span>}

        {selectMode ? (
          <span className={`select-check${isSelected ? " is-checked" : ""}`} aria-hidden="true">
            <i className="bi bi-check-lg" />
          </span>
        ) : (
          <button
            className="delete-btn"
            aria-label={`Delete ${item.filename}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <i className="bi bi-trash3" />
          </button>
        )}
      </div>
    </div>
  );
}
